import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Space, Tag, Modal, Form, message, Typography } from 'antd';
import { SearchOutlined, EditOutlined, SyncOutlined } from '@ant-design/icons';
import axiosInstance from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const { Title, Text } = Typography;

/**
 * Componente per la gestione dei codici associazione
 * Mostra una tabella di tutte le associazioni con i loro codici
 * Permette di filtrare e modificare i codici associazione
 */
const AssociationCodeManager = () => {
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentAssociation, setCurrentAssociation] = useState(null);
  const [form] = Form.useForm();
  const [stats, setStats] = useState({ total: 0, valid_codes: 0, invalid_codes: 0 });

  const { user } = useAuth();

  // Verifica che l'utente sia admin
  const isAdmin = user && user.role === 'admin';

  // Carica i dati delle associazioni
  const loadAssociations = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/association-data');
      if (response.data && response.data.success) {
        setAssociations(response.data.associations);
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Errore nel caricamento delle associazioni:', error);
      message.error('Impossibile caricare le associazioni. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadAssociations();
    }
  }, [isAdmin]);

  // Filtra le associazioni in base al testo di ricerca
  const filteredAssociations = associations.filter(assoc => {
    const searchLower = searchText.toLowerCase();
    return (
      assoc.name.toLowerCase().includes(searchLower) ||
      (assoc.association_code && assoc.association_code.toLowerCase().includes(searchLower)) ||
      (assoc.city && assoc.city.toLowerCase().includes(searchLower))
    );
  });

  // Apre il modal di modifica
  const showEditModal = (association) => {
    setCurrentAssociation(association);
    form.setFieldsValue({
      association_code: association.association_code || '',
      name: association.name || '',
      fiscal_code: association.fiscal_code || '',
      email: association.email || '',
      city: association.city || '',
      province: association.province || ''
    });
    setEditModalVisible(true);
  };

  // Gestisce il salvataggio delle modifiche
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // Validazione del codice associazione (deve iniziare con una lettera maiuscola seguita da numeri)
      if (values.association_code && !/^[A-Z][0-9]+$/.test(values.association_code)) {
        message.error('Il codice associazione deve iniziare con una lettera maiuscola seguita da numeri (es. S310490)');
        return;
      }

      const response = await axiosInstance.patch(
        `/association-data/${currentAssociation.id}`,
        values
      );

      if (response.data && response.data.success) {
        message.success('Dati associazione aggiornati con successo');
        setEditModalVisible(false);
        loadAssociations(); // Ricarica i dati aggiornati
      }
    } catch (error) {
      console.error('Errore nel salvataggio dei dati:', error);
      message.error('Errore nel salvataggio dei dati. Riprova.');
    }
  };

  // Definizione delle colonne della tabella
  const columns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Codice Associazione',
      dataIndex: 'association_code',
      key: 'association_code',
      render: (text, record) => {
        if (!text || !record.code_valid) {
          return <Tag color="red">{record.display_code || 'Mancante'}</Tag>;
        }
        return <Tag color="green">{text}</Tag>;
      },
      sorter: (a, b) => {
        // Mette i codici mancanti in fondo
        if (!a.association_code && !b.association_code) return 0;
        if (!a.association_code) return 1;
        if (!b.association_code) return -1;
        return a.association_code.localeCompare(b.association_code);
      },
    },
    {
      title: 'Codice Fiscale',
      dataIndex: 'fiscal_code',
      key: 'fiscal_code',
    },
    {
      title: 'Città',
      dataIndex: 'city',
      key: 'city',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: text => text || '-',
    },
    {
      title: 'Azioni',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => showEditModal(record)}
        >
          Modifica
        </Button>
      ),
    },
  ];

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <Title level={4}>Gestione Codici Associazione</Title>

              <div className="row mb-4">
                <div className="col-md-6">
                  <Text>
                    I codici associazione devono essere nel formato <strong>S310490</strong> (lettera maiuscola seguita da numeri).
                    Usa questa pagina per correggere i codici mancanti o non validi.
                  </Text>
                </div>
                <div className="col-md-6">
                  <div className="d-flex justify-content-end">
                    <Space>
                      <Button
                        icon={<SyncOutlined />}
                        onClick={loadAssociations}
                        loading={loading}
                      >
                        Ricarica
                      </Button>
                      <Input
                        placeholder="Cerca associazione..."
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        prefix={<SearchOutlined />}
                        style={{ width: 250 }}
                        allowClear
                      />
                    </Space>
                  </div>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-12">
                  <Space>
                    <Tag color="blue">Totale: {stats.total}</Tag>
                    <Tag color="green">Codici validi: {stats.valid_codes}</Tag>
                    <Tag color="red">Codici mancanti/invalidi: {stats.invalid_codes}</Tag>
                  </Space>
                </div>
              </div>

              <Table
                columns={columns}
                dataSource={filteredAssociations}
                rowKey="id"
                loading={loading}
                pagination={{
                  pageSize: 20,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modal per la modifica dell'associazione */}
      <Modal
        title="Modifica Dati Associazione"
        open={editModalVisible}
        onOk={handleSave}
        onCancel={() => setEditModalVisible(false)}
        okText="Salva"
        cancelText="Annulla"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Nome Associazione"
            rules={[{ required: true, message: 'Inserisci il nome dell\'associazione' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="association_code"
            label="Codice Associazione"
            rules={[
              {
                pattern: /^[A-Z][0-9]+$/,
                message: 'Il formato deve essere una lettera maiuscola seguita da numeri (es. S310490)'
              }
            ]}
            tooltip="Il codice associazione deve iniziare con una lettera maiuscola seguita da numeri (es. S310490)"
          >
            <Input placeholder="Es. S310490" />
          </Form.Item>

          <Form.Item
            name="fiscal_code"
            label="Codice Fiscale"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { type: 'email', message: 'Inserisci un indirizzo email valido' }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="city"
            label="Città"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="province"
            label="Provincia"
          >
            <Input maxLength={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AssociationCodeManager;
